import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ProgramSlideCanvas, {
  cloneSlideLayout,
  type ProgramSlideCanvasHandle,
} from '../program/ProgramSlideCanvas';
import {
  EMPTY_SLIDE_LAYOUT,
  type ProgramSlideLayoutV1,
} from '../../types/programContentSlideCanvas';
import { revokeAllPendingInLayouts } from '../../utils/slideImagePendingUpload';

const BOARD_FOOTER_H_VAR = '--cp-dash-board-footer-h';

const DEFAULT_TEXT = '#141414';
const DEFAULT_BG = '#ffffff';

function makeEmptyPage(): ProgramSlideLayoutV1 {
  return {
    ...cloneSlideLayout(EMPTY_SLIDE_LAYOUT),
    defaultTextColorHex: DEFAULT_TEXT,
    defaultBackgroundColorHex: DEFAULT_BG,
    pageTextColorHex: DEFAULT_TEXT,
    pageBackgroundColorHex: DEFAULT_BG,
  };
}

/**
 * 메인 프레젠테이션「칠판」— 프로그램 슬라이드와 동일한 캔버스 UX.
 * DB 저장 없음. 탭을 닫으면 메모리 데이터와 함께 사라짐.
 */
const EphemeralPresentationWhiteboard: React.FC<{
  /** true면 슬라이드 보기모드(퀴즈·버튼 등 보기 동작, 편집 툴바 숨김) */
  canvasViewMode?: boolean;
  /** 현재 페이지 변경 알림(호스트가 페이지별 판서를 고름) */
  onActivePageChange?: (pageIndex: number) => void;
  /** 페이지 삭제 알림(호스트가 페이지별 판서 인덱스를 당김) */
  onPageDeleted?: (removedPageIndex: number) => void;
}> = ({ canvasViewMode = false, onActivePageChange, onPageDeleted }) => {
  const [pageCount, setPageCount] = useState(1);
  const [activeIndex, setActiveIndex] = useState(0);
  const [layouts, setLayouts] = useState<Record<number, ProgramSlideLayoutV1>>(() => ({
    0: makeEmptyPage(),
  }));
  const layoutsRef = useRef(layouts);
  layoutsRef.current = layouts;
  const pageCountRef = useRef(pageCount);
  pageCountRef.current = pageCount;
  const canvasHandleRef = useRef<ProgramSlideCanvasHandle | null>(null);
  const footerNavRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      revokeAllPendingInLayouts(layoutsRef.current);
    };
  }, []);

  /* 자막·웹캠 오버레이가 하단 네비에 딱 맞도록 실측 높이를 CSS 변수로 공유 (칠판 표시 중일 때만) */
  useLayoutEffect(() => {
    const el = footerNavRef.current;
    if (!el) return undefined;
    const host = el.closest('.cp-dash-presentation-board-host');
    const page = el.closest('.cp-dash-presentation-page') as HTMLElement | null;
    const target = page ?? document.documentElement;
    const sync = () => {
      if (host?.hasAttribute('hidden')) return;
      const h = Math.ceil(el.getBoundingClientRect().height);
      target.style.setProperty(BOARD_FOOTER_H_VAR, `${Math.max(h, 1)}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [pageCount, canvasViewMode, activeIndex]);

  const currentLayout = cloneSlideLayout(layouts[activeIndex] ?? makeEmptyPage());

  const handleLayoutChange = useCallback((pageIndex: number, next: ProgramSlideLayoutV1) => {
    setLayouts((prev) => ({
      ...prev,
      [pageIndex]: {
        ...next,
        defaultTextColorHex: next.defaultTextColorHex ?? DEFAULT_TEXT,
        defaultBackgroundColorHex: next.defaultBackgroundColorHex ?? DEFAULT_BG,
      },
    }));
  }, []);

  const addPage = useCallback(() => {
    if (canvasViewMode) return;
    const newIndex = pageCountRef.current;
    setLayouts((prev) => ({ ...prev, [newIndex]: makeEmptyPage() }));
    setPageCount((c) => c + 1);
    setActiveIndex(newIndex);
  }, [canvasViewMode]);

  const deleteActivePage = useCallback(() => {
    if (canvasViewMode) return;
    const count = pageCountRef.current;
    if (count <= 1) return;
    const removeAt = activeIndex;
    setLayouts((prev) => {
      const next: Record<number, ProgramSlideLayoutV1> = {};
      let write = 0;
      for (let i = 0; i < count; i++) {
        if (i === removeAt) continue;
        next[write] = prev[i] ?? makeEmptyPage();
        write += 1;
      }
      return next;
    });
    setPageCount((c) => c - 1);
    setActiveIndex((i) => Math.max(0, Math.min(i, count - 2)));
    onPageDeleted?.(removeAt);
  }, [activeIndex, canvasViewMode, onPageDeleted]);

  useEffect(() => {
    onActivePageChange?.(activeIndex);
  }, [activeIndex, onActivePageChange]);

  const goPrev = useCallback(() => {
    setActiveIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((i) => Math.min(pageCountRef.current - 1, i + 1));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable) return;
      }
      if (canvasHandleRef.current?.tryConsumeArrowKeyForSelection(e)) return;
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  return (
    <div className="cp-program-slide-root cp-dash-presentation-board" data-slide-surface="light">
      <div className="cp-program-slide-frame cp-dash-presentation-board-frame">
        <div className="cp-program-slide-body-scroll">
          <div className="cp-program-slide-body-stage">
            <ProgramSlideCanvas
              key={`${activeIndex}-${canvasViewMode ? 'view' : 'edit'}`}
              ref={canvasHandleRef}
              layout={currentLayout}
              readOnly={false}
              viewMode={canvasViewMode}
              allLayoutsForWebpageThumbCache={layouts}
              defaultTextColorHex={currentLayout.pageTextColorHex ?? DEFAULT_TEXT}
              canvasBackgroundColorHex={currentLayout.pageBackgroundColorHex ?? DEFAULT_BG}
              onLayoutChange={(next) => handleLayoutChange(activeIndex, next)}
            />
          </div>
        </div>

        <div className="cp-program-slide-footer-nav" ref={footerNavRef}>
          <button
            type="button"
            className="cp-program-slide-nav-btn"
            onClick={goPrev}
            disabled={activeIndex <= 0}
            aria-label="이전 페이지"
          >
            &lt;
          </button>
          <div className="cp-program-slide-footer-nav-center">
            <div className="cp-program-slide-dots" role="tablist" aria-label="칠판 페이지">
              {Array.from({ length: pageCount }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === activeIndex}
                  className={'cp-program-slide-dot' + (i === activeIndex ? ' is-active' : '')}
                  onClick={() => setActiveIndex(i)}
                  title={`${i + 1}페이지`}
                >
                  <span className="cp-program-slide-dot-label">{i + 1}</span>
                </button>
              ))}
            </div>
            {!canvasViewMode ? (
              <div className="cp-program-slide-page-actions" role="group" aria-label="페이지 추가 및 삭제">
                <button
                  type="button"
                  className="cp-program-slide-page-add"
                  onClick={addPage}
                  aria-label="페이지 추가"
                  title="페이지 추가 (저장되지 않는 임시 칠판)"
                >
                  <span
                    className="cp-program-slide-page-add-icon"
                    aria-hidden
                    style={{ WebkitMaskImage: 'url(/Plus.svg)', maskImage: 'url(/Plus.svg)' }}
                  />
                </button>
                <button
                  type="button"
                  className="cp-program-slide-page-delete"
                  onClick={deleteActivePage}
                  disabled={pageCount <= 1}
                  aria-label="현재 페이지 삭제"
                  title={pageCount <= 1 ? '마지막 페이지는 삭제할 수 없습니다.' : '현재 페이지 삭제'}
                >
                  <span
                    className="cp-program-slide-page-delete-icon"
                    aria-hidden
                    style={{ WebkitMaskImage: 'url(/Minus.svg)', maskImage: 'url(/Minus.svg)' }}
                  />
                </button>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="cp-program-slide-nav-btn"
            onClick={goNext}
            disabled={activeIndex >= pageCount - 1}
            aria-label="다음 페이지"
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
  );
};

export default EphemeralPresentationWhiteboard;
